import React, { useState, useMemo } from 'react';
import { Message as MessageType } from '../../types';
import { Check, CheckCheck, XCircle, FileText, Music, Video, Download, Reply, Loader2 } from 'lucide-react';

// 扩展 Message 类型以包含 interactive_payload
interface ExtendedMessage extends MessageType {
  interactive_payload?: {
    type?: string;
    header?: { type?: string; image?: { link?: string }; text?: string };
    body?: { text?: string };
    footer?: { text?: string };
    action?: { parameters?: { display_text?: string; url?: string }; cards?: any[] };
  };
}

// 临时定义LocationData类型
interface LocationData {
  lat: number;
  lon: number;
  name?: string;
  address?: string;
}

interface MessageProps {
  message: ExtendedMessage;
  onReply?: (message: ExtendedMessage) => void;
  onQuoteClick?: (quotedMessage: ExtendedMessage['quoted_message']) => void;
  onRetry?: (message: ExtendedMessage) => void;
}

const formatTimestamp = (isoString: string) => {
  const date = new Date(isoString);
  const now = new Date();

  // 使用 UTC 时间（阿克拉时区 GMT+0）
  const msgDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const today = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));

  // 获取时间部分
  const time = date.toLocaleTimeString('en-US', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  // 判断日期
  const msgDateOnly = new Date(msgDate.getFullYear(), msgDate.getMonth(), msgDate.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diffDays = Math.floor((todayOnly.getTime() - msgDateOnly.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    // 今天：只显示时间
    return time;
  } else if (diffDays === 1) {
    // 昨天
    return `昨天 ${time}`;
  } else {
    // 更早：显示日期格式 YYYY-M-D
    const year = msgDate.getFullYear();
    const month = msgDate.getMonth() + 1;
    const day = msgDate.getDate();
    return `${year}-${month}-${day} ${time}`;
  }
};

const MessageStatus: React.FC<{ status: 'sending' | 'sent' | 'delivered' | 'read' | 'failed'; onRetry?: () => void }> = ({ status, onRetry }) => {
  switch (status) {
    case 'sending':
      return <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />;
    case 'sent':
      return <Check className="w-4 h-4 text-muted-foreground" />;
    case 'delivered':
      return <CheckCheck className="w-4 h-4 text-muted-foreground" />;
    case 'read':
      return <CheckCheck className="w-4 h-4 text-whatsapp-blue" />;
    case 'failed':
      return (
        <span title="点击重发" onClick={(e) => { e.stopPropagation(); onRetry?.(); }}>
          <XCircle className="w-4 h-4 text-destructive cursor-pointer hover:scale-125 transition-transform" />
        </span>
      );
    default:
      return null;
  }
};

const MediaPreview: React.FC<{ message: ExtendedMessage }> = ({ message }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const renderIcon = () => {
    switch (message.type) {
      case 'audio': return <Music className="w-8 h-8 text-foreground" />;
      case 'video': return <Video className="w-8 h-8 text-foreground" />;
      case 'document': return <FileText className="w-8 h-8 text-foreground" />;
      default: return null;
    }
  }

  if (message.type === 'image' && message.content) {
    // 解析图片内容（支持JSON格式和纯URL）
    let imageUrl = '';
    let caption = '';

    try {
      // 尝试解析JSON格式 {"url": "...", "caption": "..."}
      const parsed = JSON.parse(message.content);
      imageUrl = parsed.url || '';
      caption = parsed.caption || '';
    } catch {
      // 如果不是JSON，当作纯URL处理
      imageUrl = message.content;
    }

    if (!imageUrl) {
      return <div className="text-muted-foreground p-2">[图片加载失败]</div>;
    }

    return (
      <>
        <img
          src={imageUrl}
          alt="图片"
          className="max-w-[600px] w-full rounded-lg cursor-pointer"
          style={{ imageRendering: 'auto' }}
          onClick={() => setIsExpanded(true)}
          loading="lazy"
        />
        {caption && (
          <p className="mt-2 text-sm whitespace-pre-wrap">{caption}</p>
        )}
        {isExpanded && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
            onClick={() => setIsExpanded(false)}
          >
            <img src={imageUrl} alt="图片预览" className="max-w-[80vw] max-h-[80vh] object-contain" />
          </div>
        )}
      </>
    );
  }

  // Interactive 消息渲染（包括 carousel）
  if (message.type === 'interactive' && message.content) {
    try {
      const interactive = JSON.parse(message.content);
      const interactiveType = interactive?.type;

      // Carousel 类型
      if (interactiveType === 'carousel') {
        const cards = interactive?.action?.cards || [];
        const bodyText = interactive?.body?.text || '';

        return (
          <div className="space-y-2">
            {bodyText && (
              <p className="text-sm font-medium">{bodyText}</p>
            )}
            <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
              {cards.map((card: any, index: number) => {
                const headerType = card?.header?.type;
                const mediaLink = card?.header?.[headerType]?.link || '';
                const cardBody = card?.body?.text || '';
                const ctaText = card?.action?.parameters?.display_text || 'View';
                const ctaUrl = card?.action?.parameters?.url || '#';

                return (
                  <div key={index} className="flex-shrink-0 w-48 rounded-lg border border-border overflow-hidden bg-card">
                    {headerType === 'image' && mediaLink && (
                      <img
                        src={mediaLink}
                        alt={cardBody || `Card ${index + 1}`}
                        className="w-full h-32 object-cover cursor-pointer"
                        onClick={() => setIsExpanded(true)}
                      />
                    )}
                    {headerType === 'video' && mediaLink && (
                      <video
                        src={mediaLink}
                        controls
                        className="w-full h-32 object-cover"
                      />
                    )}
                    {cardBody && (
                      <p className="p-2 text-xs text-muted-foreground truncate">{cardBody}</p>
                    )}
                    <a
                      href={ctaUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="block p-2 text-center text-xs text-primary hover:bg-primary/10 border-t border-border"
                    >
                      {ctaText}
                    </a>
                  </div>
                );
              })}
            </div>
            {/* 点击放大预览 */}
            {isExpanded && cards.length > 0 && (
              <div
                className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
                onClick={() => setIsExpanded(false)}
              >
                <img
                  src={cards[0]?.header?.image?.link || cards[0]?.header?.video?.link}
                  alt="预览"
                  className="max-w-[80vw] max-h-[80vh] object-contain"
                />
              </div>
            )}
          </div>
        );
      }

      // CTA URL 类型
      if (interactiveType === 'cta_url') {
        const header = interactive?.header;
        const body = interactive?.body?.text || '';
        const footer = interactive?.footer?.text || '';
        const ctaText = interactive?.action?.parameters?.display_text || 'View';
        const ctaUrl = interactive?.action?.parameters?.url || '#';

        return (
          <div className="rounded-lg border border-border overflow-hidden bg-card max-w-[300px]">
            {header?.type === 'image' && header?.image?.link && (
              <img src={header.image.link} alt="" className="w-full h-32 object-cover" />
            )}
            {header?.type === 'text' && header?.text && (
              <p className="p-2 font-medium text-sm">{header.text}</p>
            )}
            {body && <p className="px-3 py-2 text-sm">{body}</p>}
            {footer && <p className="px-3 pb-2 text-xs text-muted-foreground">{footer}</p>}
            <a
              href={ctaUrl}
              target="_blank"
              rel="noreferrer"
              className="block p-3 text-center text-sm text-primary hover:bg-primary/10 border-t border-border font-medium"
            >
              {ctaText}
            </a>
          </div>
        );
      }

      // 其他 interactive 类型，显示原始内容
      return (
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-sm text-muted-foreground">[Interactive Message: {interactiveType || 'unknown'}]</p>
        </div>
      );
    } catch (e) {
      return (
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-sm text-muted-foreground">[Interactive Message]</p>
        </div>
      );
    }
  }

  return (
    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
      {message.type === 'video' ? (
        <video src={message.content} controls className="max-w-[600px] w-full rounded-lg" />
      ) : message.type === 'audio' ? (
        <audio src={message.content} controls className="w-full" />
      ) : message.type === 'location' ? (
        <div className="bg-muted/50 rounded-lg p-3 min-w-[200px]">
          {(() => {
            // 解析位置信息，支持多种格式
            let locationData: LocationData = { lat: 0, lon: 0, name: '', address: '' };

            if (typeof message.content === 'object') {
              locationData = message.content as LocationData;
            } else if (typeof message.content === 'string') {
              // 解析字符串格式: "lat,lon (Name - Address)"
              const match = message.content.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)(?:\s*\((.+?)\))?$/);
              if (match) {
                locationData = {
                  lat: parseFloat(match[1]),
                  lon: parseFloat(match[2]),
                  name: '',
                  address: ''
                };

                if (match[3]) {
                  const parts = match[3].split(' - ');
                  if (parts.length === 2) {
                    locationData.name = parts[0].trim();
                    locationData.address = parts[1].trim();
                  } else {
                    locationData.name = parts[0].trim();
                  }
                }
              }
            }

            const mapUrl = `https://www.google.com/maps?q=${locationData.lat},${locationData.lon}`;
            const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${locationData.lat},${locationData.lon}&zoom=15&size=400x200&markers=color:red%7C${locationData.lat},${locationData.lon}&key=YOUR_API_KEY`;

            return (
              <>
                {/* 地图预览区域 */}
                <div className="bg-accent/20 rounded-md p-4 mb-2 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-2">
                      <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-xs text-muted-foreground">位置共享</p>
                  </div>
                </div>

                {/* 位置信息 */}
                {locationData.name && (
                  <p className="font-medium text-sm mb-1">{locationData.name}</p>
                )}
                {locationData.address && (
                  <p className="text-xs text-muted-foreground mb-2">{locationData.address}</p>
                )}
                {!locationData.name && !locationData.address && (
                  <p className="text-xs text-muted-foreground mb-2">
                    {locationData.lat.toFixed(6)}, {locationData.lon.toFixed(6)}
                  </p>
                )}

                {/* 打开地图按钮 */}
                <a
                  href={mapUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  在地图中查看
                </a>
              </>
            );
          })()}
        </div>
      ) : (
        <>
          {renderIcon()}
          <div className="flex-1">
            <p className="font-medium truncate">{message.content}</p>
            <p className="text-xs text-muted-foreground">文件</p>
          </div>
          <a href={message.content} target="_blank" rel="noreferrer" className="p-2 rounded-full hover:bg-secondary">
            <Download className="w-5 h-5 text-foreground" />
          </a>
        </>
      )}
    </div>
  );
}

const Message: React.FC<MessageProps> = ({ message, onReply, onQuoteClick, onRetry }) => {
  const isMe = message.sender === 'me';
  const status = (message.status || 'sent') as 'sending' | 'sent' | 'delivered' | 'read' | 'failed';

  const handleQuoteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (message.quoted_message && onQuoteClick) {
      onQuoteClick(message.quoted_message);
    }
  };

  return (
    <div className={`flex ${isMe ? 'justify-end' : 'justify-start'} group`}>
      <div
        className={`relative max-w-lg px-4 py-2 rounded-lg shadow ${isMe
          ? 'bg-whatsapp-green/30 text-foreground rounded-br-none'
          : 'bg-card text-foreground rounded-bl-none'
          }`}
      >
        {message.quoted_message && (
          <div
            className="bg-black/20 p-2 rounded-md border-l-2 border-accent mb-2 text-sm cursor-pointer hover:bg-black/30 transition-colors"
            onClick={handleQuoteClick}
            title="跳转到引用消息"
          >
            <p className="font-bold text-accent">{message.quoted_message.sender === 'me' ? '我' : '用户'}</p>
            <p className="text-muted-foreground truncate">{message.quoted_message.content}</p>
          </div>
        )}

        {message.type === 'text' ? (
          (() => {
            const raw = String(message.content || '');

            // 检查是否是 Flow 订单消息 - 不再显示，因为后端会发送单独的确认消息
            try {
              const parsed = JSON.parse(raw);
              if (parsed.type === 'flow_order') {
                // Flow 订单消息不显示任何内容，订单信息已通过 "✅ Order Created Successfully!" 消息展示
                return null;
              }
              if (parsed.type === 'flow_response') {
                return (
                  <div className="bg-blue-900/20 rounded-lg p-3 min-w-[200px]">
                    <p className="font-medium text-blue-400 mb-1">{parsed.name || 'Flow Response'}</p>
                    <p className="text-sm">{parsed.body}</p>
                  </div>
                );
              }
            } catch {
              // 不是 JSON，继续正常处理
            }

            // 检查 interactive_payload 是否包含 CTA URL 消息
            const payload = (message as any).interactive_payload;
            if (payload && payload.type === 'cta_url') {
              const headerImg = payload.header?.type === 'image' ? payload.header?.image?.link : null;
              const headerText = payload.header?.type === 'text' ? payload.header?.text : null;
              const bodyText = payload.body?.text || '';
              const footerText = payload.footer?.text || '';
              const ctaText = payload.action?.parameters?.display_text || 'View';
              const ctaUrl = payload.action?.parameters?.url || '#';
              return (
                <div className="overflow-hidden rounded-lg bg-[#202c33] text-[#e9edef] max-w-[300px]">
                  {headerImg && (
                    <img src={headerImg} alt="" className="w-full h-32 object-cover" onError={(e) => (e.currentTarget.style.display = 'none')} />
                  )}
                  {headerText && (
                    <div className="px-3 pt-3 font-bold">{headerText}</div>
                  )}
                  {bodyText && (
                    <div className="px-3 py-2 whitespace-pre-wrap text-sm">{bodyText}</div>
                  )}
                  {footerText && (
                    <div className="px-3 pb-2 text-xs text-gray-400">{footerText}</div>
                  )}
                  <div className="border-t border-[#2a3942] p-3 flex items-center justify-center">
                    <a href={ctaUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#25D366] text-white hover:brightness-110 text-sm font-medium">
                      {ctaText}
                    </a>
                  </div>
                </div>
              );
            }

            const isCTA = !!message.cta || /^\[CTA\]/i.test(raw) || /^CTA:/i.test(raw);
            if (isCTA) {
              const url = message.cta?.url || (raw.match(/https?:\/\/\S+/i)?.[0] || '');
              const display = message.cta?.displayText || raw.replace(/^\[CTA\]\s*/i, '').replace(/^CTA:\s*/i, '').replace(url, '').replace(/-\s*$/i, '').trim();
              return (
                <div className="overflow-hidden rounded-lg bg-[#202c33] text-[#e9edef]">
                  {message.ctaHeader?.type === 'image' && message.ctaHeader.content && (
                    <img src={message.ctaHeader.content} alt="CTA头图" className="w-full h-auto" onError={(e) => (e.currentTarget.style.display = 'none')} />
                  )}
                  {message.ctaHeader?.type === 'text' && message.ctaHeader.content && (
                    <div className="px-3 pt-3 font-bold">{message.ctaHeader.content}</div>
                  )}
                  {message.ctaBody && (
                    <div className="px-3 py-2 whitespace-pre-wrap">{message.ctaBody}</div>
                  )}
                  {message.ctaFooter && (
                    <div className="px-3 pb-2 text-xs text-gray-400">{message.ctaFooter}</div>
                  )}
                  <div className="border-t border-[#2a3942] p-3 flex items-center justify-center">
                    <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#25D366] text-white hover:brightness-110">
                      {display || '打开链接'}
                    </a>
                  </div>
                </div>
              );
            }
            return <p className="whitespace-pre-wrap">{message.content}</p>;
          })()
        ) : message.type === 'image' || message.type === 'video' || message.type === 'audio' || message.type === 'document' || message.type === 'interactive' ? (
          <MediaPreview message={message} />
        ) : message.type === 'location' ? (
          <MediaPreview message={message} />
        ) : (
          <p className="whitespace-pre-wrap">{message.content}</p>
        )}

        <div className="flex items-center justify-end mt-1 space-x-1">
          <span className="text-xs text-muted-foreground">
            {formatTimestamp(message.timestamp)}
          </span>
          {isMe && <MessageStatus status={status} onRetry={onRetry ? () => onRetry(message) : undefined} />}
        </div>

        {/* Hover Action for Reply */}
        {onReply && (
          <button
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 bg-black/20 rounded-full text-white hover:bg-black/40 transition-all z-10"
            onClick={(e) => {
              e.stopPropagation();
              onReply(message);
            }}
            title="回复此消息"
          >
            <Reply className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Message;
